<template>
  <Dialog
    v-model:visible="visible"
    modal
    :closable="canClose"
    :closeOnEscape="canClose"
    :dismissableMask="canClose"
    :style="{ width: '50vw' }"
    header="Прогресс синхронизации"
  >
    <div v-if="progress" class="sync-progress-container">
      <!-- Сообщение когда синхронизация не запущена -->
      <div v-if="normalizedStage === 'idle' || !normalizedStage" class="text-center p-5">
        <i class="pi pi-inbox text-6xl text-400 mb-3"></i>
        <h3 class="text-600 mb-2">Синхронизация не запущена</h3>
        <p class="text-500 mb-0">
          Нажмите кнопку "Синхронизировать всё" чтобы начать синхронизацию товаров с PartsSoft
        </p>
        <!-- Кнопка продолжить если есть checkpoint -->
        <div v-if="hasCheckpoint" class="mt-4">
          <div class="p-3 border-round bg-blue-50 mb-3">
            <div class="flex align-items-center gap-2 mb-2">
              <i class="pi pi-pause-circle text-blue-600 text-xl"></i>
              <span class="font-semibold text-blue-700">Есть незавершённая синхронизация</span>
            </div>
            <p class="text-sm text-600 mb-0">
              Синхронизация была приостановлена. Вы можете продолжить с того места, где остановились.
            </p>
          </div>
          <Button
            label="Продолжить синхронизацию"
            icon="pi pi-play"
            class="p-button-success"
            :loading="isResuming"
            @click="resumeSync"
          />
        </div>
      </div>
      
      <!-- Состояние паузы -->
      <div v-else-if="isPaused" class="text-center p-5">
        <i class="pi pi-pause-circle text-6xl text-orange-500 mb-3"></i>
        <h3 class="text-orange-700 mb-2">Синхронизация на паузе</h3>
        <p class="text-600 mb-3">
          Синхронизация приостановлена. Вы можете продолжить в любой момент.
        </p>
        <div class="grid text-sm mb-4">
          <div class="col-6">
            <span class="text-600">Обработано:</span>
            <span class="font-semibold ml-2">{{ getProcessedCount() }}</span>
          </div>
          <div class="col-6">
            <span class="text-600">Этап:</span>
            <span class="font-semibold ml-2">{{ getPausedStage() }}</span>
          </div>
        </div>
        <Button
          label="Продолжить синхронизацию"
          icon="pi pi-play"
          class="p-button-success"
          :loading="isResuming"
          @click="resumeSync"
        />
      </div>
      
      <!-- Ожидание паузы -->
      <div v-else-if="isPausing" class="text-center p-5">
        <ProgressSpinner style="width: 60px; height: 60px" />
        <h3 class="text-orange-700 mb-2 mt-3">Остановка синхронизации...</h3>
        <p class="text-600 mb-0">
          Ожидание завершения текущего товара/батча. Это может занять несколько секунд.
        </p>
      </div>
      
      <!-- Активная синхронизация -->
      <div v-else>
        <!-- Общий прогресс -->
        <div class="mb-4">
          <div class="flex justify-content-between mb-2">
            <span class="font-semibold">Общий прогресс</span>
            <span class="text-600">Этап {{ progress.current_step }} из 7</span>
          </div>
          <ProgressBar :value="overallProgress" :showValue="false" />
        </div>

        <!-- Текущий этап -->
        <div class="mb-4 p-3 border-round bg-primary-50">
          <div class="flex align-items-center gap-2 mb-2">
            <i :class="getStageIcon(normalizedStage)" class="text-primary"></i>
            <span class="font-semibold text-primary">{{ getStageName(normalizedStage) }}</span>
          </div>
          <p v-if="currentStageData" class="text-sm text-600 mb-0">
            {{ currentStageData.message || 'В процессе...' }}
          </p>
        </div>

        <!-- Детали этапов -->
        <div class="stages-details">
        <!-- Категории -->
        <StageItem
          title="1. Синхронизация категорий"
          :stage-data="progress.categories"
          stage-name="categories"
        />

        <!-- Атрибуты -->
        <StageItem
          title="2. Обновление атрибутов"
          :stage-data="progress.attributes"
          stage-name="attributes"
        />

        <!-- Товары (последовательно) -->
        <StageItem
          title="3. Новые товары и исправление ошибок"
          :stage-data="progress.products_sequential"
          stage-name="products_sequential"
        />

        <!-- Товары (батчами) -->
        <StageItem
          title="4. Обновление существующих товаров"
          :stage-data="progress.products_batch"
          stage-name="products_batch"
        />

        <!-- Изображения категорий -->
        <StageItem
          title="5. Изображения категорий"
          :stage-data="progress.category_images"
          stage-name="category_images"
        />

        <!-- Очистка пустых категорий -->
        <StageItem
          title="6. Очистка пустых категорий"
          :stage-data="progress.categories_cleanup"
          stage-name="categories_cleanup"
        />

        <!-- Отчет -->
        <StageItem
          title="7. Генерация отчета"
          :stage-data="progress.report"
          stage-name="report"
        />
      </div>

      <!-- Статистика завершения -->
      <div v-if="isCompleted" class="mt-4 p-3 border-round bg-green-50">
        <div class="flex align-items-center gap-2 mb-2">
          <i class="pi pi-check-circle text-green-600 text-xl"></i>
          <span class="font-semibold text-green-700">Синхронизация завершена!</span>
        </div>
        <div class="grid text-sm">
          <div class="col-4">
            <span class="text-600">Успешно:</span>
            <span class="font-semibold ml-2 text-green-700">{{ getTotalSuccess() }}</span>
          </div>
          <div class="col-4">
            <span class="text-600">Ошибок:</span>
            <span class="font-semibold ml-2" :class="getTotalFailed() > 0 ? 'text-red-600' : ''">{{ getTotalFailed() }}</span>
          </div>
          <div v-if="getTotalNoChanges() > 0" class="col-4">
            <span class="text-600">Без изменений:</span>
            <span class="font-semibold ml-2 text-500">{{ getTotalNoChanges() }}</span>
          </div>
        </div>
        <!-- Детализация: добавлено / обновлено -->
        <div v-if="getSummaryCreated() > 0 || getSummaryUpdated() > 0" class="grid text-sm mt-2 pt-2 border-top-1 border-green-200">
          <div v-if="getSummaryCreated() > 0" class="col-4">
            <span class="text-600">Добавлено:</span>
            <span class="font-semibold ml-2 text-blue-600">{{ getSummaryCreated() }}</span>
          </div>
          <div v-if="getSummaryUpdated() > 0" class="col-4">
            <span class="text-600">Обновлено:</span>
            <span class="font-semibold ml-2 text-orange-600">{{ getSummaryUpdated() }}</span>
          </div>
        </div>
      </div>

      <!-- Ошибка -->
      <div v-if="isFailed" class="mt-4 p-3 border-round bg-red-50">
        <div class="flex align-items-center gap-2">
          <i class="pi pi-times-circle text-red-600 text-xl"></i>
          <span class="font-semibold text-red-700">Синхронизация прервана</span>
        </div>
        <p v-if="currentStageData" class="text-sm text-600 mb-0 mt-2">
          {{ currentStageData.message }}
        </p>
      </div>
      </div><!-- /v-else активная синхронизация -->
    </div><!-- /progress container -->

    <div v-else class="text-center p-4">
      <ProgressSpinner />
      <p class="mt-3 text-600">Загрузка данных о прогрессе...</p>
    </div>

    <template #footer>
      <div class="flex justify-content-between align-items-center w-full">
        <!-- Левая часть: Статус -->
        <div class="text-sm text-600">
          <span v-if="isPaused">На паузе</span>
          <span v-else-if="isPausing">Остановка...</span>
          <span v-else-if="isRunning">
            Синхронизация активна
            <span v-if="totalEta" class="ml-2">~ {{ totalEta }}</span>
          </span>
          <span v-else-if="isCompleted">Завершено</span>
          <span v-else-if="isFailed">Ошибка</span>
        </div>

        <!-- Правая часть: Кнопки -->
        <div class="flex gap-2">
          <!-- Кнопка Скрыть (всегда доступна) -->
          <Button
            label="Скрыть"
            icon="pi pi-eye-slash"
            class="p-button-text"
            @click="visible = false"
          />

          <!-- Кнопка Пауза (только на этапах products_sequential/products_batch) -->
          <Button
            v-if="canPause && isAdmin"
            label="Пауза"
            icon="pi pi-pause"
            class="p-button-warning"
            :loading="isPauseRequesting"
            @click="requestPause"
          />

          <!-- Кнопка Принудительная остановка (если на паузе или зависло) -->
          <Button
            v-if="(isPaused || isPausing) && isAdmin"
            label="Сбросить"
            icon="pi pi-ban"
            class="p-button-danger p-button-outlined"
            :loading="isForceStopping"
            @click="forceStop"
            v-tooltip.top="'Сбросить состояние синхронизации и начать с чистого листа'"
          />

          <!-- Кнопка Продолжить (только если на паузе) -->
          <Button
            v-if="isPaused && isAdmin"
            label="Продолжить"
            icon="pi pi-play"
            class="p-button-success"
            :loading="isResuming"
            @click="resumeSync"
          />
          
          <!-- Кнопка Закрыть (если завершено) -->
          <Button
            v-if="isCompleted || isFailed"
            label="Закрыть"
            icon="pi pi-check"
            @click="visible = false"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, computed, watch, onUnmounted, onMounted } from 'vue'
import Dialog from 'primevue/dialog'
import ProgressBar from 'primevue/progressbar'
import Button from 'primevue/button'
import ProgressSpinner from 'primevue/progressspinner'
import StageItem from './StageItem.vue'
import apiService from '@/services/apiService'
import { useToast } from 'primevue/usetoast'
import { parseUtc, formatDuration } from '@/utils/dateHelpers'
import { useEtaTimer } from '@/composables/useEtaTimer'

const toast = useToast()

const props = defineProps({
  storeId: {
    type: Number,
    required: true
  },
  visible: {
    type: Boolean,
    default: false
  },
  isAdmin: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:visible', 'completed'])

const visible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
})

const progress = ref(null)
const pauseState = ref(null)
const isPauseRequesting = ref(false)
const isResuming = ref(false)
const isForceStopping = ref(false)
let pollInterval = null
let pollErrorCount = ref(0) // BUG-19: Счётчик ошибок polling

const normalizedStage = computed(() => {
  const stage = progress.value?.stage
  return stage ? stage.toLowerCase() : null
})

// Проверка состояний паузы
const isPaused = computed(() => {
  return normalizedStage.value === 'paused' || pauseState.value?.is_paused
})

const isPausing = computed(() => {
  return normalizedStage.value === 'pausing' || pauseState.value?.pause_requested
})

const canPause = computed(() => {
  if (!progress.value) return false
  const stage = normalizedStage.value
  // Можно ставить на паузу только на этапах обработки товаров
  return (stage === 'products_sequential' || stage === 'products_batch') && 
         progress.value.is_running && 
         !isPausing.value && 
         !isPaused.value
})

const hasCheckpoint = computed(() => {
  return pauseState.value?.has_checkpoint
})

const canClose = computed(() => {
  // Всегда разрешаем закрывать (скрывать) окно
  return true
})

const isRunning = computed(() => {
  return normalizedStage.value !== 'idle' && 
         normalizedStage.value !== 'completed' && 
         normalizedStage.value !== 'failed' && 
         normalizedStage.value !== 'paused' &&
         !isPaused.value
})

// Вычисляемые свойства
const currentStageData = computed(() => {
  if (!progress.value) return null
  const stage = normalizedStage.value
  const stageMap = {
    'categories': progress.value.categories,
    'attributes': progress.value.attributes,
    'products_sequential': progress.value.products_sequential,
    'products_batch': progress.value.products_batch,
    'category_images': progress.value.category_images,
    'categories_cleanup': progress.value.categories_cleanup,
    'generating_report': progress.value.report
  }
  return stage ? stageMap[stage] || null : null
})

const overallProgress = computed(() => {
  if (!progress.value) return 0
  return (progress.value.current_step / 7) * 100
})

const isCompleted = computed(() => {
  return normalizedStage.value === 'completed'
})

const isFailed = computed(() => {
  return normalizedStage.value === 'failed'
})

// ETA таймер
const { now: etaNow } = useEtaTimer(3000)

const totalEta = computed(() => {
  const _tick = etaNow.value
  if (!progress.value || !isRunning.value) return null

  // Находим текущий активный этап
  const stages = ['products_sequential', 'products_batch']
  for (const key of stages) {
    const stage = progress.value[key]
    if (!stage || stage.status !== 'running') continue
    if (!stage.started_at || !stage.total) continue

    const processed = stage.processed || 0
    if (processed <= 0) continue

    const startedAt = parseUtc(stage.started_at)
    const elapsedSec = (Date.now() - startedAt.getTime()) / 1000
    if (elapsedSec <= 0) continue

    const speed = processed / elapsedSec
    const remaining = (stage.total - processed) / speed
    if (remaining <= 0) continue

    return formatDuration(remaining)
  }
  return null
})

// Методы
const fetchProgress = async () => {
  try {
    const response = await apiService.get(`/sync/products/progress/${props.storeId}`)
    progress.value = response.data
    pollErrorCount.value = 0 // Сброс счётчика ошибок при успехе
    
    // Если синхронизация завершена или провалена, останавливаем опрос
    if (isCompleted.value || isFailed.value) {
      stopPolling()
      emit('completed', progress.value)
    }
    
    // Если на паузе, тоже останавливаем
    if (isPaused.value) {
      stopPolling()
    }
    
    // BUG-16 FIX: Обновляем pauseState периодически
    if (progress.value && progress.value.stage !== 'idle') {
      await fetchPauseState()
    }
  } catch (error) {
    // BUG-19 FIX: Учёт ошибок и graceful degradation
    pollErrorCount.value++
    console.error(`Ошибка получения прогресса (attempt ${pollErrorCount.value}):`, error)
    
    // Если синхронизация не найдена (404), создаем пустой прогресс
    if (error.response?.status === 404) {
      progress.value = {
        stage: 'idle',
        current_step: 0,
        message: 'Синхронизация не запущена'
      }
      stopPolling()
      return
    }
    
    // Если 10+ ошибок подряд — прекратить polling, показать предупреждение
    if (pollErrorCount.value >= 10) {
      stopPolling()
      toast.add({
        severity: 'warn',
        summary: 'Потеря связи',
        detail: 'Не удаётся получить статус синхронизации. Проверьте подключение и обновите страницу.',
        life: 10000
      })
    }
  }
}

const fetchPauseState = async () => {
  try {
    const response = await apiService.get(`/sync/products/pause-state/${props.storeId}`)
    pauseState.value = response.data
  } catch (error) {
    console.error('Ошибка получения состояния паузы:', error)
  }
}

const requestPause = async () => {
  isPauseRequesting.value = true
  try {
    await apiService.post(`/sync/products/pause/${props.storeId}`)
    toast.add({
      severity: 'info',
      summary: 'Пауза запрошена',
      detail: 'Синхронизация остановится после обработки текущего товара/батча',
      life: 5000
    })
    // Обновить прогресс
    await fetchProgress()
  } catch (error) {
    console.error('Ошибка запроса паузы:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.detail || 'Не удалось поставить на паузу',
      life: 5000
    })
  } finally {
    isPauseRequesting.value = false
  }
}

const resumeSync = async () => {
  isResuming.value = true
  try {
    await apiService.post(`/sync/products/resume/${props.storeId}`)
    toast.add({
      severity: 'success',
      summary: 'Синхронизация возобновлена',
      detail: 'Продолжение с места остановки',
      life: 3000
    })
    // Начать опрос прогресса
    startPolling()
  } catch (error) {
    console.error('Ошибка возобновления:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.detail || 'Не удалось возобновить синхронизацию',
      life: 5000
    })
  } finally {
    isResuming.value = false
  }
}

const forceStop = async () => {
  if (!confirm('Вы уверены? Это полностью сбросит состояние синхронизации. Запуск с чистого листа.')) return
  
  isForceStopping.value = true
  try {
    await apiService.post(`/sync/products/force-stop/${props.storeId}`)
    toast.add({
      severity: 'warn',
      summary: 'Синхронизация сброшена',
      detail: 'Состояние очищено. Можно запустить синхронизацию заново.',
      life: 5000
    })
    // Обновить состояние
    await fetchPauseState()
    await fetchProgress()
    // BUG-8 FIX: Оповестить родителя что синхронизация остановлена
    emit('completed', progress.value)
  } catch (error) {
    console.error('Ошибка принудительной остановки:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.detail || 'Не удалось сбросить синхронизацию',
      life: 5000
    })
  } finally {
    isForceStopping.value = false
  }
}

const startPolling = () => {
  if (pollInterval) return
  
  fetchProgress() // Сразу получаем данные
  fetchPauseState() // Получаем состояние паузы
  pollInterval = setInterval(fetchProgress, 2000) // Опрос каждые 2 секунды
}

const stopPolling = () => {
  if (pollInterval) {
    clearInterval(pollInterval)
    pollInterval = null
  }
}

const closeDialog = () => {
  visible.value = false
  stopPolling()
  progress.value = null
}

const getStageName = (stage) => {
  const names = {
    'idle': 'Ожидание',
    'categories': 'Синхронизация категорий',
    'attributes': 'Обновление атрибутов',
    'products_sequential': 'Новые товары и ошибки',
    'products_batch': 'Обновление товаров батчами',
    'category_images': 'Изображения категорий',
    'categories_cleanup': 'Очистка категорий',
    'generating_report': 'Генерация отчета',
    'completed': 'Завершено',
    'failed': 'Ошибка',
    'pausing': 'Остановка...',
    'paused': 'На паузе'
  }
  const key = stage ? stage.toLowerCase() : ''
  return names[key] || stage || 'Неизвестно'
}

const getStageIcon = (stage) => {
  const icons = {
    'idle': 'pi pi-clock',
    'categories': 'pi pi-sitemap',
    'attributes': 'pi pi-tags',
    'products_sequential': 'pi pi-box',
    'products_batch': 'pi pi-sync',
    'category_images': 'pi pi-image',
    'categories_cleanup': 'pi pi-trash',
    'generating_report': 'pi pi-file',
    'completed': 'pi pi-check-circle',
    'failed': 'pi pi-times-circle',
    'pausing': 'pi pi-spin pi-spinner',
    'paused': 'pi pi-pause-circle'
  }
  const key = stage ? stage.toLowerCase() : ''
  return icons[key] || 'pi pi-spin pi-spinner'
}

const getTotalSuccess = () => {
  if (!progress.value) return 0
  return (progress.value.products_sequential?.success || 0) +
         (progress.value.products_batch?.success || 0)
}

const getTotalFailed = () => {
  if (!progress.value) return 0
  return (progress.value.products_sequential?.failed || 0) +
         (progress.value.products_batch?.failed || 0)
}

const getTotalNoChanges = () => {
  if (!progress.value) return 0
  return (progress.value.products_batch?.no_changes || 0)
}

const getSummaryCreated = () => {
  if (!progress.value?.summary) return 0
  return progress.value.summary.created_or_synced || 0
}

const getSummaryUpdated = () => {
  if (!progress.value?.summary) return 0
  return progress.value.summary.updated_existing || 0
}

const getProcessedCount = () => {
  if (!progress.value) return 0
  return (progress.value.products_sequential?.processed || 0) +
         (progress.value.products_batch?.processed || 0)
}

const getPausedStage = () => {
  const stage = progress.value?.checkpoint_stage
  if (stage === 'sequential') return 'Создание товаров'
  if (stage === 'batch') return 'Обновление товаров'
  return 'Неизвестно'
}

// Наблюдатели
watch(() => props.visible, (newVal) => {
  if (newVal) {
    fetchPauseState() // Сначала проверяем состояние паузы
    startPolling()
  } else {
    stopPolling()
  }
})

// При монтировании проверяем состояние и запускаем polling если visible
onMounted(() => {
  if (props.visible) {
    fetchPauseState()
    startPolling() // FIX: запуск polling при монтировании с visible=true
  }
})

// Очистка при размонтировании
onUnmounted(() => {
  stopPolling()
})
</script>

<style scoped>
.sync-progress-container {
  min-height: 300px;
}

.stages-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* Мобильная адаптация */
@media (max-width: 768px) {
  .sync-progress-container .grid .col-4,
  .sync-progress-container .grid .col-6 {
    width: 100%;
    flex: 0 0 100%;
  }
}
</style>
